'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getRodents, type Building } from '@repo/api';
import { PLACEHOLDER_REPORT_DATA } from '../../context/constants';
import { RodentInfestationSectionSkeleton } from './RodentInfestationSectionSkeleton';
import { RodentInfestationSection } from './RodentInfestationSection';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';

interface RodentInfestationProps {
  building: Building;
  buildingAddress: string;

  showSkeleton: boolean; // parent (explorer/addReport flow)
  isReportReady: boolean; // true only when addReport done or hasAccess
  shouldBlur?: boolean; // if you still need it elsewhere
}

function RodentInfestation({
  building,
  buildingAddress,
  showSkeleton,
  isReportReady,
}: RodentInfestationProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [baseRodentData, setBaseRodentData] = useState<any | null>(null);
  const [baseLoading, setBaseLoading] = useState(true);
  const [baseError, setBaseError] = useState<string | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_rodent_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  // reset when building change

  useEffect(() => {
    // keep skeleton up until addReport is ready
    if (!isReportReady) {
      setBaseLoading(false);
      setBaseRodentData(PLACEHOLDER_REPORT_DATA);
      return;
    }

    (async () => {
      setBaseLoading(true);
      setBaseError(null);

      try {
        const response = await getRodents({ buildingId: building.id });
        setBaseRodentData(response);
      } catch (err: any) {
        setBaseError(err?.message || 'Failed to load rodent data');
      } finally {
        setBaseLoading(false);
      }
    })();
  }, [isReportReady, building.id]);

  // ✅ Single skeleton condition
  const shouldShowSkeleton = showSkeleton || baseLoading;

  if (shouldShowSkeleton) {
    return <RodentInfestationSectionSkeleton />;
  }

  return (
    <div ref={sectionRef}>
      <RodentInfestationSection
        buildingId={building.id}
        buildingSlug={building.slug}
        buildingAddress={buildingAddress}
        isBlurRequired={!isReportReady}
        baseRodentData={baseRodentData}
        baseLoading={baseLoading}
        baseError={baseError}
      />
    </div>
  );
}

export default RodentInfestation;
